/**
 * cardText.ts — A CARD RENDERS THE STRING, NOT THE ESCAPE (2026-08-25)
 * ====================================================================
 *
 * THE DEFECT THIS FILE ANSWERS
 * ----------------------------
 * Live on the acquisition door, 2026-08-25, session b2e8b74f. The caller asked
 * "can you show it to me?", the Order Summary card fired, and what she read was
 *
 *     Name: Erika Truscott\nProperty: Major's Place, Unit 2\nPlan: 1 Gig
 *
 * with the backslash and the n printed on the glass. The card was RIGHT about
 * every fact on it and still looked broken, which is the worst kind of wrong: a
 * caller cannot tell "our renderer is unpolished" apart from "these people do not
 * have my details straight".
 *
 * THE EXACT HOP, because "somewhere in the pipeline" is not a diagnosis
 * ---------------------------------------------------------------------
 * A card payload reaches the browser down one of two paths and BOTH of them end
 * at the single `onShowCard` callback in VoiceOverlay:
 *
 *   MODEL PATH     show_card is client-owned (useVapi's CLIENT_OWNED_TOOLS). The
 *                  model authors `description` as a field of a JSON *string* in
 *                  `toolCall.function.arguments`. To put a line break in it the
 *                  model must write the two characters \ and n — that is the only
 *                  way a newline can exist inside a JSON string literal. Vapi's
 *                  SDK hands us that arguments blob already parsed ONCE, and a
 *                  model that writes `\\n` (escaping defensively, which they do
 *                  when the value is assembled as text rather than emitted as
 *                  JSON) survives that parse as a literal backslash-n. Nothing
 *                  downstream unescaped it.
 *   TOOL PATH      captureCards reads a server tool's `result`. Vapi relays the
 *                  result as a JSON string; parseMaybeJson parses it once, with
 *                  the same double-escape exposure the moment a server
 *                  `instruction`/`description` carries a newline.
 *
 * So the escape is not introduced by one buggy stringify — it is what survives a
 * single parse of a value that was escaped twice. That is why the fix is ONE
 * normalisation at the ONE convergence point rather than a patch per producer:
 * every future card inherits it, and a tool that adopts the tool-card envelope
 * gets it with no change here.
 *
 * SECOND HALF, AND IT IS NOT OPTIONAL: A REAL NEWLINE STILL HAD NOWHERE TO GO
 * ---------------------------------------------------------------------------
 * Unescaping alone would have turned the visible \n into an INVISIBLE one: HTML
 * collapses whitespace, so a correctly-parsed multi-line description rendered as
 * one run-on line ("Name: Erika Truscott Property: Major's Place"), and the
 * description slot clamps at two lines, so a six-line order summary lost four of
 * them silently. Both halves live with this file's caller — see GenericCard's
 * description block, which now renders `pre-line` and clamps to the lines it
 * actually holds.
 *
 * WHAT IS DELIBERATELY NOT DONE HERE
 * ----------------------------------
 * This normalises WHITESPACE ESCAPES and nothing else. It does not rewrite the
 * value, re-case it, re-punctuate it, or "tidy" it. A validated value is spoken
 * and shown exactly as the tool returned it (conduct rules 33/34) and this file
 * is not a licence to edit one — a confirmation number that arrives with odd
 * spacing keeps it, because that spacing is the tool's pacing, not our noise.
 *
 * ── VENDORED COPY (2026-08-25) ──────────────────────────────────────────────
 * ora-core's src/lib/cardText.ts is the SOURCE; this is the operator dock's copy,
 * kept logic-identical. `components/canonical/orbi/` is a known fork of the
 * ora-core web app (see CLAUDE.md, "Known duplication"), and the escaped-newline
 * defect is in BOTH renderers because both inherited the same description slot.
 * Land a change in ora-core first, then copy it here. The only permitted
 * difference is the GenericCardPayload import: the dock declares its card payload
 * inline on VoiceOverlayPanel's OrbiCardModel rather than in a cardGuard module it
 * does not have, and its payload has no `options` slot yet.
 *
 * THE HOP IS OrbiDock.tsx's `pushCard`, not the fork's src/components/VoiceOverlay.tsx.
 * That file is DEAD in this repo - nothing imports it, the bundle entry is entry.tsx ->
 * OrbiDock.tsx -> VoiceOverlayPanel - so a fix applied there would ship nothing. pushCard
 * is the single funnel: useVapi's show_card handler and the imperative window.OrbiDock
 * .showCard() handle both go through it.
 *
 * File: components/canonical/orbi/src/lib/cardText.ts
 */

import type { OrbiCardModel } from '../VoiceOverlayPanel';

/** The dock declares its card payload inline on OrbiCardModel; this is that shape. */
type GenericCardPayload = OrbiCardModel['payload'] & {
  options?: Array<{ label: string; detail?: string }>;
};

/**
 * Turn the escape SEQUENCES a JSON round-trip leaves behind into the characters
 * they stand for.
 *
 * Only the four whitespace escapes are recognised, and `\\` is honoured as an
 * escaped backslash so a value that legitimately ends in one is not eaten. The
 * single left-to-right pass matters: on `\\n` the regex consumes `\\` first and
 * emits one backslash, leaving `n` as the literal letter it was meant to be —
 * a two-pass "replace \\n then replace \n" would wrongly turn it into a newline.
 */
function unescapeWhitespace(s: string): string {
  return s.replace(/\\(\\|n|r|t)/g, (_m, c: string) =>
    c === '\\' ? '\\' : c === 'n' ? '\n' : c === 'r' ? '\r' : '\t',
  );
}

/**
 * The one text normaliser every card field passes through.
 *
 * Returns null for anything that is not a non-empty string once normalised, so a
 * field that was only ever escape characters disappears rather than rendering as
 * a blank line — cardGuard's founding rule (absence renders nothing) applied to
 * the inside of a field.
 */
export function normalizeCardText(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const out = unescapeWhitespace(v)
    // CRLF and a lone CR both mean "new line" to a person reading a card; the DOM
    // only honours \n, and a stray \r renders as nothing at all.
    .replace(/\r\n?/g, '\n')
    // Trailing spaces on a line are invisible in the source and become a ragged
    // right edge under `pre-line`.
    .split('\n').map((line) => line.replace(/[ \t]+$/, '')).join('\n')
    // A model that separates fields with a blank line is fine; one that emits
    // four of them is padding a card with air it has no facts to fill.
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return out === '' ? null : out;
}

/** How many lines this text will actually occupy. Used to size the clamp. */
export function cardTextLineCount(s: string): number {
  return s.split('\n').length;
}

/**
 * Normalise every caller-visible string on a card payload, in place of the
 * payload rather than on top of it.
 *
 * Applied at the single hop both firing paths converge on (VoiceOverlay's
 * onShowCard), BEFORE admitCard and BEFORE cardFactKey: the fact key is
 * fingerprinted from what appears on screen, so keying it off un-normalised text
 * would let the same fact re-render just because one firing escaped it and the
 * next did not.
 *
 * Non-string fields (amount, currency, intent, icon, cta.action) are passed
 * through untouched — they are identifiers and numbers, not prose.
 */
export function normalizeCardPayload(
  payload: GenericCardPayload | null | undefined,
): GenericCardPayload | null {
  if (!payload) return null;
  const out: GenericCardPayload = { ...payload };

  const heading = normalizeCardText(payload.heading);
  const metric = normalizeCardText(payload.metric);
  const description = normalizeCardText(payload.description);
  if (heading !== null) out.heading = heading; else delete out.heading;
  if (metric !== null) out.metric = metric; else delete out.metric;
  if (description !== null) out.description = description; else delete out.description;

  if (payload.badges) {
    // A badge is a pill: it is one short phrase by construction, so a newline in
    // one is noise rather than structure and collapses to a space. A badge left
    // with no text at all is dropped — an empty pill is a card apologising for
    // itself in miniature.
    out.badges = payload.badges
      .map((b) => ({ ...b, text: (normalizeCardText(b.text) ?? '').replace(/\n/g, ' ') }))
      .filter((b) => b.text !== '');
    if (out.badges.length === 0) delete out.badges;
  }

  if (payload.options) {
    out.options = payload.options
      .map((o) => {
        const label = (normalizeCardText(o.label) ?? '').replace(/\n/g, ' ');
        const detail = normalizeCardText(o.detail);
        return detail === null ? { label } : { label, detail: detail.replace(/\n/g, ' ') };
      })
      .filter((o) => o.label !== '');
    if (out.options.length === 0) delete out.options;
  }

  if (payload.cta) {
    const label = (normalizeCardText(payload.cta.label) ?? '').replace(/\n/g, ' ');
    if (label === '') delete out.cta;
    else out.cta = { ...payload.cta, label };
  }

  return out;
}
